import axios from 'axios'
import { catchError, config, returnData } from './api-utils'

/**
 * Send a message to the chat service and receive streaming responses via SSE
 * @param {Object} params - Parameters
 * @param {string} params.message - User message
 * @param {number|null} params.conversationId - Conversation ID (null for new conversation)
 * @param {string} params.scopeCode - Customer or company scope code
 * @param {Object} params.context - Frontend route context for the chat request
 * @param {Function} params.onStatus - Callback for status events (optional)
 * @param {Function} params.onContent - Callback for content events (required)
 * @param {Function} params.onSources - Callback for sources events (optional)
 * @param {Function} params.onSuggestions - Callback for suggestions events (optional)
 * @param {Function} params.onComplete - Callback for completion event (optional)
 * @param {Function} params.onTitle - Callback for title events (optional, new conversations only)
 * @param {Function} params.onError - Callback for error events (optional)
 * @returns {Function} Abort function to cancel the request
 */
/**
 * Turns an ASP.NET problem+json validation body into the same shape an SSE error event has, so the
 * caller cannot tell the two apart. Returns null for anything that is not one.
 * @returns {Object|null}
 */
const readValidationProblem = (body) => {
  let problem = null

  try {
    problem = JSON.parse(body)
  } catch (err) {
    return null
  }

  if (!problem || typeof problem !== 'object' || !problem.errors) return null

  const messages = Object.values(problem.errors)
    .flat()
    .filter((message) => typeof message === 'string')

  return {
    type: 'error',
    content: messages.join(' ') || problem.title || 'Invalid input',
    errorCode: 'INVALID_INPUT',
    errorDetails: problem.errors,
  }
}

/**
 * Pulls the SSE `error` event out of a non-OK response body.
 * The chat endpoint reports pre-flight failures (unknown conversation, and any other failure that
 * happens before streaming starts) with a real HTTP status code and an error event in the body.
 * Without this the specific error code would be lost behind a generic network error.
 * @returns {Object|null} the error event payload, or null if the body carries no parsable error
 */
const readSseErrorEvent = async (response) => {
  let body = ''

  try {
    body = await response.text()
  } catch (err) {
    return null
  }

  // ASP.NET's [ApiController] rejects an invalid DTO before the action runs, so a validation
  // failure never reaches the SSE writer and comes back as problem+json instead. Parsed here rather
  // than left to fall through: without this the loop below finds no SSE event, the caller throws,
  // and the user was told "connection lost" for a message that was simply too long.
  const validationError = readValidationProblem(body)
  if (validationError) return validationError

  for (const message of body.split('\n\n')) {
    if (!message.trim()) continue

    let eventType = 'message'
    let data = null

    for (const line of message.split('\n')) {
      if (line.startsWith('event:')) {
        eventType = line.substring(6).trim()
      } else if (line.startsWith('data:')) {
        try {
          data = JSON.parse(line.substring(5).trim())
        } catch (err) {
          data = null
        }
      }
    }

    if (eventType === 'error' && data) return data
  }

  return null
}

const sendMessage = ({
  message,
  conversationId,
  scopeCode,
  context,
  onStatus,
  onContent,
  onSources,
  onSuggestions,
  onComplete,
  onTitle,
  onStreamEnd,
  onError,
}) => {
  const requestBody = {
    message,
    conversationId: conversationId ?? null,
  }

  const controller = new AbortController()
  const { signal } = controller

  const apiConfig = config()
  const serializedContext = context ? JSON.stringify(context) : null

  // Root-level chat has no customer or company, so scopeCode is null there. Interpolating it
  // straight into the path produced a request to ".../null/chat"; the backend exposes the
  // unscoped "api/chat" route for exactly this case.
  const scopeSegment = scopeCode ? `${scopeCode}/` : ''

  fetch(`${apiConfig.baseURL}${scopeSegment}chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...apiConfig.headers,
      ...(serializedContext
        ? { 'x-finazilla-chat-context': serializedContext }
        : {}),
    },
    body: JSON.stringify(requestBody),
    signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        const errorEvent = await readSseErrorEvent(response)
        if (errorEvent) {
          onError?.(errorEvent)
          return
        }

        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          break
        }

        buffer += decoder.decode(value, { stream: true })

        const messages = buffer.split('\n\n')
        buffer = messages.pop()

        for (const message of messages) {
          if (!message.trim()) continue

          try {
            const lines = message.split('\n')
            let eventType = 'message'
            let data = null

            for (const line of lines) {
              if (line.startsWith('event:')) {
                eventType = line.substring(6).trim()
              } else if (line.startsWith('data:')) {
                const jsonString = line.substring(5).trim()
                data = JSON.parse(jsonString)
              }
            }

            if (!data) continue

            switch (eventType) {
              case 'status':
                onStatus?.(data)
                break
              case 'content':
                onContent?.(data)
                break
              case 'sources':
                onSources?.(data)
                break
              case 'suggestions':
                onSuggestions?.(data)
                break
              case 'complete':
                onComplete?.(data)
                break
              case 'title':
                onTitle?.(data)
                break
              case 'error':
                onError?.(data)
                break
              default:
                console.warn('Unknown SSE event type:', eventType, data)
            }
          } catch (err) {
            console.error('Error parsing SSE message:', message, err)
          }
        }
      }

      onStreamEnd?.()
    })
    .catch((error) => {
      if (error.name === 'AbortError') {
        return
      }

      console.error('Fetch error:', error)
      onError?.({
        type: 'error',
        content: error.message || 'An unexpected error occurred',
        errorCode: 'NETWORK_ERROR',
      })
    })

  return () => controller.abort()
}

const getConversations = (scopeCode) =>
  axios
    .get(`${scopeCode}/chat/conversations`, config())
    .then((res) => res.data?.data ?? [])

const getConversation = (scopeCode, conversationId) =>
  axios
    .get(`${scopeCode}/chat/conversations/${conversationId}`, config())
    .then((res) => res.data?.data ?? res.data)

const deleteConversation = (scopeCode, conversationId) =>
  axios
    .delete(`${scopeCode}/chat/conversations/${conversationId}`, config())
    .then((res) => res.data)

// Documentation index (System users only)
const getIndexStatus = () =>
  axios
    .get('chat/index/status', config())
    .then(returnData)
    .catch(catchError)

const syncIndex = () =>
  axios
    .post('chat/index/sync', null, config())
    .then(returnData)
    .catch(catchError)

const chatApi = {
  sendMessage,
  getConversations,
  getConversation,
  deleteConversation,
  getIndexStatus,
  syncIndex,
}

export default chatApi
